import { useEffect, useRef, useState } from 'react';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Input, TextInput } from '@mantine/core';
import { useIntl } from 'react-intl';

import { rateSearchInputMessages } from './rate-search-input-messages';
import styles from './RateSearchInput.module.css';

type RateSearchInputProps = {
  isOpen: boolean;
  searchTerm: string;
  onOpen: () => void;
  onClose: () => void;
  onSearchChange: (value: string) => void;
};

export const RateSearchInput = ({ isOpen, searchTerm, onOpen, onClose, onSearchChange }: RateSearchInputProps) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(searchTerm);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    setInputValue(searchTerm);
  }, [searchTerm]);

  const handleChange = (value: string) => {
    setInputValue(value);
    onSearchChange(value);
  };

  const handleClear = () => {
    setInputValue('');
    onSearchChange('');
  };

  if (!isOpen) {
    return (
      <ActionIcon
        aria-label={intl.formatMessage(rateSearchInputMessages.searchAriaLabel)}
        className={styles.searchTrigger}
        data-testid='rate-search-trigger'
        onClick={onOpen}
        variant='ghost'
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </ActionIcon>
    );
  }

  return (
    <TextInput
      className={styles.searchInput}
      data-testid='rate-search-input'
      leftSection={<FontAwesomeIcon icon={faMagnifyingGlass} />}
      onChange={(event) => handleChange(event.currentTarget.value)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
      placeholder={intl.formatMessage(rateSearchInputMessages.searchPlaceholder)}
      ref={inputRef}
      rightSection={inputValue !== '' ? <Input.ClearButton onClick={handleClear} /> : undefined}
      rightSectionPointerEvents='all'
      size='xs'
      value={inputValue}
    />
  );
};
